<script>
  import { push, querystring, replace } from "svelte-spa-router"
  import { onDestroy, onMount } from "svelte"
  import notify from "../../../Script/notify"
  import { queryStringToJSON } from "../../../Script/queryString"
  import { copyToClipboard } from "../../Publish/SinglePublish/script"
  import { loadJs } from "../../../Script/loadLibrary"
  import { drawSign } from "./Script/sign"
  import ApplicationState from "../../../Stores/applicationStates"
  import { getRequest, patchRequest } from "../../../Script/apiMethods"
  import StaticSign from "./Components/staticSign.svelte"
  import PageControl from "./Components/pageControl.svelte"
  import Timeline from "./Components/timeline.svelte"
  import ConfirmationModal from "../../Publish/BulkUpload/Shared/confirmationModal.svelte"
  import SinglePublish from "../../Publish/SinglePublish/Stores/singlePublishStore"
  import LoadingScreen from "../../Publish/SinglePublish/Components/loadingScreen.svelte"
  import ShareComponent from "../../../Shared/shareComponent.svelte"
  import { loadQR } from "../../Publish/BulkUpload/Script/bulkUpload"
  import { getCertData } from "./Script/fetchCertDetails"
  import CnApproveModal from "./Components/cnApproveModal.svelte"

  /**
   * @type {HTMLCanvasElement}
   */
  let canvas
  /**
   * @typedef {Object} closeNotification
   * @property {Function} close - Closes the notification.
   */
  let closeNotification = {
    close() {},
  }

  let showPDFLoader = true

  let totalPages

  let _PDFDOC

  let currentpage = 1

  let pageNumPending = null

  let query = {}

  let users = []

  let pdfDimensions = {}

  let showLoader = false

  let qrImage = ""

  let showConfirmationModal = false

  let loaderMsg = "Fetching Docket Details"

  let docket = {}

  let certData = []

  // let isSigningStarted = false

  let isModeNotSelected = false

  let isESignPending = false

  let disableResendBtn = false

  let isApproversFound = false

  let resendTimer

  let statusTimer

  let currentRenderTask = null
  let openCNModal = false

  // 5 Minutes
  const STATUS_UPDATE_TIMER = 300000

  onMount(async () => {
    try {
      // Prevent execution if unauthorized (because pop() is asynchronous)
      if (!$ApplicationState.pdfEnabled || !$ApplicationState.msignToken) return

      showLoader = true
      query = queryStringToJSON($querystring)
      if (!query.id) throw "Document ID not found"

      await fetchDocket()

      if (docket.status == "SIGNED") {
        const { txidPublished } = await fetchDocumentDetails(docket.documentID)

        if (txidPublished) {
          await updateDocket()
          docket.status = "COMPLETED"
          qrImage = await loadQR(docket.url)
        }
      }

      if (docket.status == "PENDING" || docket.status == "APPROVED") getTimelyStatus()
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: onMount"
      closeNotification = notify.danger(error, info)
      push("/signing/dockets")
    } finally {
      showLoader = false
    }
  })

  const getTimelyStatus = () => {
    statusTimer = setInterval(async () => {
      try {
        showLoader = true
        await fetchDocket()
      } catch (error) {
        console.error(error)
        clearInterval(statusTimer)
        const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While checking and updating docket status every 5 minutes"
        notify.danger(error, info)
      } finally {
        showLoader = false
      }
    }, STATUS_UPDATE_TIMER)
  }

  onDestroy(() => {
    clearInterval(statusTimer)
  })

  const fetchDocket = async () => {
    try {
      const { orgName, created, status, name, uuid, url, documentID, signees, id, esign, signCnMismatchID } = await fetchDocketDetails(query.id)

      docket = { orgName, created, status, name, uuid, url, documentID, id, esign, signCnMismatchID }

      if (docket.signCnMismatchID) notify.info("Kindly resolve the mismatch between the signer name and the Common Name (CN) in the signer certificate.")

      if (status == "COMPLETED") qrImage = await loadQR(url)
      users = [...signees]

      users = await Promise.all(
        users.map(async (user) => {
          if (user.placements.length !== 0 && user.status === "PENDING") {
            const signDetails = {
              name: user.name,
              role: user.role,
              showQR: documentID ? true : false,
            }
            user.sign = await drawSign(signDetails)
          }

          return user
        })
      )

      let approvers = users.filter((user) => user.role == "APPROVER") || []

      let signers = users.filter((user) => user.role == "SIGNER")

      arrangeUsers()

      // users = [...approvers, ...signers]

      isApproversFound = Boolean(approvers.length)

      let signersWithPreference = signers.filter((signer) => !signer.internal && !signer.mode && signer.status == "PENDING") || []
      // console.log(signersWithPreference)
      isModeNotSelected = Boolean(signersWithPreference.length)

      let signersWithPendingESign = signers.filter((signer) => !signer.internal && signer.mode == "E-SIGN" && signer.status == "PENDING") || []
      isESignPending = Boolean(signersWithPendingESign.length)

      let fileData = await fetchFile()

      await handleCertDetailsFetching(fileData)

      let fileURL = URL.createObjectURL(fileData)

      await loadPDF(fileURL)
    } catch (error) {
      throw error
    }
  }

  const arrangeUsers = () => {
    const approvedUsers = []
    const pendingApprovers = []
    const signedUsers = []
    const pendingSigners = []

    for (const user of users) {
      if (user.role === "APPROVER") {
        if (user.status === "PENDING") pendingApprovers.push(user)
        else approvedUsers.push(user)
      }
      if (user.role === "SIGNER") {
        if (user.status === "PENDING") pendingSigners.push(user)
        else signedUsers.push(user)
      }
    }

    approvedUsers.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))
    signedUsers.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp))

    users = [...approvedUsers, ...pendingApprovers, ...signedUsers, ...pendingSigners]
  }

  const fetchDocketDetails = async (id) => {
    try {
      const REQ_URL = `/api/pdf-signing/v2/docket/${id}?listSignee=true`
      const CONFIG = {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      }

      const { docket } = await getRequest(REQ_URL, CONFIG)
      // console.log({ docket })

      return docket
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const fetchFile = async () => {
    try {
      const FILEPATH = `/api/pdf-signing/v2/docket/${query.id}/file`
      const CONFIG = {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
        responseType: "blob",
      }

      const response = await getRequest(FILEPATH, CONFIG)

      return response
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleCertDetailsFetching = async (response) => {
    try {
      const file = new File([response], "file.pdf", { type: "application/pdf" })
      const SIGNING_MODES = new Set(["AADHAAR-ESIGN", "PFX-SIGN", "DSC-TOKEN-SIGN"])

      if (users.some((u) => u.role === "SIGNER" && u.status !== "PENDING" && SIGNING_MODES.has(u.mode))) {
        try {
          certData = await getCertData(file)
          await extractOIDsFromCertData(certData)
        } catch (err) {
          console.error("Failed to fetch certificate data:", err)
          certData = []
          // throw err
        }
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }
  const extractOIDsFromCertData = async (certData) => {
    try {
      if (!certData) return []

      const str = JSON.stringify(certData)

      const matches = str.match(/\b\d+(\.\d+)+\b/g)

      const uniqueOIDs = [...new Set(matches)]
      console.log({ uniqueOIDs })

      // await sendErrorInfo("OID detected", { oids: uniqueOIDs })

      return uniqueOIDs
    } catch (error) {
      console.error(error)
    }
  }

  const loadPDF = async (url) => {
    try {
      // Set the loading flag to true to indicate that the PDF is loading
      showPDFLoader = true

      // Load the pdf.js library if it's not already loaded
      await loadJs("pdfjsLib", "assets/lib/pdf.js")

      // Load the pdf.js worker if it's not already loaded
      await loadJs("pdfjsWorker", "assets/lib/pdf.worker.js")

      // Specify the workerSrc property for pdf.js
      pdfjsLib.GlobalWorkerOptions.workerSrc = "assets/lib/pdf.worker.js"

      // Start downloading the PDF asynchronously
      let loadingTask = pdfjsLib.getDocument(url)

      // Await the completion of the PDF loading task
      _PDFDOC = await loadingTask.promise

      // Get the total number of pages in the PDF document
      totalPages = _PDFDOC.numPages

      // console.log(currentpage, "currentpage")

      // console.log({ pageNumPending })

      // Display the current page of the PDF
      await showPage(currentpage)
    } catch (error) {
      throw error
    }
  }

  /**
   * Renders a specific page of the loaded PDF document onto a canvas.
   *
   * @param {number} pageno - The page number to display.
   * @returns {Promise<void>} - A promise that resolves when the page is successfully rendered.
   */
  const showPage = async (pageno) => {
    try {
      showPDFLoader = true

      pageno = Number(pageno)
      // console.log(pageno, "pageno")
      // Get the specified page from the PDF document
      let page = await _PDFDOC.getPage(pageno)

      // Get the viewport for the page at a scale of 1
      let viewport = page.getViewport({ scale: 1 })

      // Prepare the canvas using the PDF page dimensions
      let context = canvas.getContext("2d", { willReadFrequently: true })
      pdfDimensions.width = canvas.width = viewport.width
      pdfDimensions.height = canvas.height = viewport.height

      pdfDimensions.clientWidth = parseFloat(getComputedStyle(canvas).width)
      pdfDimensions.clientHeight = parseFloat(getComputedStyle(canvas).height)

      // Cancel any ongoing render task
      if (currentRenderTask && currentRenderTask.cancel) {
        currentRenderTask.cancel()
      }

      // Create the render context with canvas context and viewport
      let renderContext = {
        canvasContext: context,
        viewport: viewport,
      }

      await page.cleanup()

      // Start new render task and keep track of it
      try {
        currentRenderTask = page.render(renderContext)
        await currentRenderTask.promise
      } catch (error) {
        if (error.name === "RenderingCancelledException") {
          return
        }
        console.error("Render error:", error)
        throw error
      }

      // Set the loading flag to false to indicate that the PDF page has finished loading
      showPDFLoader = false

      // console.log("pdf loaded")

      // Check if a new page rendering is pending
      if (pageNumPending !== null) {
        // Render the pending page and reset the pending page number
        showPage(pageNumPending)
        pageNumPending = null
      }
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  /**
   * Queues a page to be rendered if a PDF page is currently being loaded.
   * If no page is being loaded, it immediately renders the specified page.
   */
  const queueRenderPage = async () => {
    try {
      // Check if a PDF page is currently being loaded
      if (showPDFLoader) {
        // Set the pending page number to the specified page number
        pageNumPending = currentpage || 1
      } else {
        // Render the specified page immediately
        showPage(currentpage || 1)
      }
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: During PDF page navigation"
      closeNotification = notify.danger(error, info)
    }
  }

  const handleCopyInvite = async (e) => {
    try {
      let text = ""
      const viewBase = $ApplicationState.viewBase.split("v2")[0]
      if (e.detail.mode) {
        text = `${viewBase}docket/mode?id=${docket.uuid}&email=${e.detail.email}&name=${e.detail.name}`
      } else {
        const path = e.detail.role == "SIGNER" ? "sign" : "approve"
        text = `${viewBase}docket/${path}?id=${docket.uuid}&email=${e.detail.email}`
      }

      await copyToClipboard(text)

      notify.success(`${e.detail.mode ? "Signing Preference" : "Invite"} Link copied successfully`)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While copying invite link"
      closeNotification = notify.danger(error, info)
    }
  }

  const handleBulkInvite = async () => {
    try {
      showLoader = true

      // Sending Mail Invite
      await getRequest(`/api/pdf-signing/v2/docket/${query.id}/mail`, {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      })

      disableResendBtn = true
      notify.success("Email resent successfully. You will be able to resend the invite after 30 seconds if needed.")

      setTimeout(() => {
        resendTimer = disableResendBtn = false
      }, 30000)
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While sending bulk invite through email"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoader = false
    }
  }

  const handleDocumentDownload = async () => {
    try {
      showLoader = true

      let fileData = await fetchFile()

      // Create a new File object with the Blob and provide a default filename and type
      let file = new File([fileData], query.id, {
        type: "application/pdf",
      })

      let a = document.createElement("a")
      a.href = URL.createObjectURL(file)
      a.download = query.id
      a.click()
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While downloading signed document"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoader = false
    }
  }

  const handleFileUpload = async () => {
    try {
      showConfirmationModal = false
      if (docket.signCnMismatchID) {
        let user = getCNMismatchUser()
        if (user) throw `Signer with name <strong>${user.name}</strong> has a Digital Certificate Common Name mismatch. Please resolve the mismatch to proceed with publishing.`
      }
      showLoader = true
      loaderMsg = "Fetching Document Details"

      const { fileHash, dataHash, txidPublished, options, metadata } = await fetchDocumentDetails(docket.documentID)

      if (!fileHash) {
        await uploadFile()
      } else {
        $SinglePublish = {}
        $SinglePublish.documentId = docket.documentID
        $SinglePublish.docketId = query.id
        $SinglePublish.fileHash = fileHash

        if (dataHash) {
          if (txidPublished) {
            await updateDocket()

            docket.status = "COMPLETED"

            qrImage = await loadQR(docket.url)
          } else {
            $SinglePublish.dataHash = dataHash
            $SinglePublish.options = JSON.parse(options)
            $SinglePublish.Json = JSON.parse(metadata)
            push(`/publish/single/preview?documentId=${docket.documentID}`)
          }
        } else {
          push(`/publish/single/choose_template?documentId=${docket.documentID}`)
        }
      }
    } catch (error) {
      console.error(error)
      if (error == "P2BCODE::DOCUMENT_DOES_NOT_EXIST") error = "P2BCODE::DOCKET_LINKED_DOC_DOES_NOT_EXIST"
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While proceeding to publish the docket after all participants have signed"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoader = false
      loaderMsg = "Fetching Docket Details"
    }
  }

  const updateDocket = async () => {
    try {
      const REQ_URL = `/api/pdf-signing/v2/docket/${query.id}`
      const CONFIG = {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      }

      await patchRequest(REQ_URL, {}, CONFIG)
    } catch (error) {
      throw error
    }
  }

  const uploadFile = async () => {
    try {
      let fileData = await fetchFile()

      // Create a new File object with the Blob and provide a default filename and type
      let file = new File([fileData], query.id, {
        type: "application/pdf",
      })

      const REQ_URL = `/api/swagger/v2/documents/${docket.documentID}/add-file`
      const REQ_BODY = {
        documentID: docket.documentID,
        file: file,
        docketID: query.id,
      }

      const CONFIG = {
        headers: {
          "Content-Type": "multipart/form-data",
          "x-access-token": $ApplicationState.token,
        },
      }

      const data = await patchRequest(REQ_URL, REQ_BODY, CONFIG)

      $SinglePublish = {}
      $SinglePublish.documentId = docket.documentID
      $SinglePublish.docketId = query.id
      $SinglePublish.fileHash = data.fileHash

      push(`/publish/single/choose_template?documentId=${docket.documentID}`)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const fetchDocumentDetails = async (id) => {
    try {
      const REQ_URL = `/api/swagger/v2/documents/${id}`
      const CONFIG = {
        headers: {
          "x-access-token": $ApplicationState.token,
        },
      }

      const { document } = await getRequest(REQ_URL, CONFIG)

      return document
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  const handleOpenCNModal = () => {
    openCNModal = true
  }

  const handleCNApproal = async (e) => {
    try {
      openCNModal = false
      showLoader = true

      const REQ_URL = `/api/pdf-signing/v2/docket/${query.id}/cn-decision`

      let REQ_BODY = {}

      if (e.detail.state === "REJECT") {
        REQ_BODY = { status: "REJECTED" }
      } else if (e.detail.state === "CHANGE_CN_NAME") {
        REQ_BODY = { signeeName: "CN" }
      }

      const CONFIG = {
        headers: {
          "msign-org-token": $ApplicationState.msignToken,
        },
      }

      await patchRequest(REQ_URL, REQ_BODY, CONFIG)
      notify.success("Docket Updated Successfully")
      await fetchDocket()
    } catch (error) {
      console.error(error)
      closeNotification.close()
      const info = "PAGE: Doc Multi-Signer (Status Page)\nEVENT: While handling CN mismatch approval"
      closeNotification = notify.danger(error, info)
    } finally {
      showLoader = false
    }
  }

  const getCNMismatchUser = () => {
    return users.find((user) => user.id == docket.signCnMismatchID)
  }

  onDestroy(() => {
    if (resendTimer) clearTimeout(resendTimer)
  })
</script>

{#if showConfirmationModal}
  <ConfirmationModal
    title="Confirmation for Publishing"
    content="I have thoroughly reviewed the signed document and am prepared to proceed to process the document for publishing."
    on:click={() => {
      showConfirmationModal = false
    }}
    on:confirm={handleFileUpload}
  />
{/if}
{#if openCNModal}
  <CnApproveModal
    on:close={() => {
      openCNModal = false
    }}
    on:handleCN={handleCNApproal}
  />
{/if}

<svelte:head>
  <title>Single Docket Status</title>
</svelte:head>

{#if showLoader}
  <LoadingScreen msg={loaderMsg} />
{/if}

<div class="flex h-full min-h-screen select-none overflow-x-hidden">
  <div class="flex max-h-screen w-full flex-col items-center justify-center bg-[#f7f7f7] dark:bg-[#292a2f] lg:w-[70%] xl:w-3/4">
    <div id="positionOverlay" class="{showPDFLoader ? 'animate-pulse bg-gray-100/10' : ''} relative flex" style="max-height: {window.innerHeight - 120}px; max-width: {window.innerWidth / 2}px ;">
      <canvas id="staticCanvas" bind:this={canvas} class="h-full w-full rounded-md" />
      {#if !showPDFLoader}
        {#each users.filter((user) => user.status == "PENDING") as user (user.id)}
          {#each user.placements as placement (placement.id)}
            <StaticSign source={user.sign} {pdfDimensions} {currentpage} {placement} />
          {/each}
        {/each}
      {/if}
    </div>

    <div class="{!showPDFLoader ? 'opacity-100' : ' opacity-0'} mt-3 flex w-full items-center justify-center gap-3">
      <PageControl bind:currentpage {totalPages} on:render={queueRenderPage} />
    </div>
  </div>

  <div class="max-h-screen w-full min-w-[350px] space-y-3 overflow-y-auto bg-white p-4 text-gray-600 dark:bg-black lg:w-[30%] xl:w-1/4 xl:p-6">
    <div class="relative w-full">
      <h1 class="mb-3 select-none text-xl font-bold text-black dark:text-white">Doc Multi-Signer</h1>

      <button
        on:click={() => {
          if (query.fromDocketList && window?.navigation?.canGoBack) window.history.back()
          else push("/signing/dockets")
        }}
        class="absolute right-0 top-0 text-red-600 active:text-red-400"
      >
        <i class="fa-solid fa-circle-xmark" />
      </button>
    </div>

    <div class="w-full space-y-3">
      <div class="flex w-full items-center justify-between gap-3 text-base font-bold text-slate-400">
        {#if docket.name}
          <span class="min-w-max text-sm font-medium">Docket Name:</span>
          <h1 class="wordBreak tracking-wide text-blue-500">{docket.name}</h1>
        {:else}
          <span class="min-w-max text-sm font-medium">Docket ID:</span>
          <h1 class="font-mono text-blue-500">{docket.id}</h1>
        {/if}
      </div>
    </div>

    {#if docket.status == "SIGNED" || docket.status == "COMPLETED"}
      <div class="space-y-3">
        <button on:click={handleDocumentDownload} type="button" class="w-full rounded-md bg-blue-500 py-1.5 px-3 text-sm text-white hover:bg-blue-600 active:bg-blue-400">
          <span>
            <i class="fa-solid fa-download" />
          </span>
          &nbsp;&nbsp;Download
        </button>
        {#if docket.documentID && docket.status == "SIGNED"}
          <button
            type="button"
            on:click={() => {
              showConfirmationModal = true
            }}
            class="flex w-full items-center justify-center rounded-md bg-green-500 py-1.5 px-3 text-sm text-white hover:bg-green-600 active:bg-green-400"
          >
            <span>
              <i class="fa-solid fa-forward" />
            </span>
            &nbsp;&nbsp;Proceed to Publish
          </button>
        {/if}
      </div>
    {/if}

    {#if docket.status == "PENDING" || docket.status == "APPROVED"}
      <button disabled={disableResendBtn} on:click={handleBulkInvite} type="button" class="w-full rounded-md bg-green-500 py-1.5 px-3 text-white hover:bg-green-600 active:bg-green-400 disabled:cursor-not-allowed disabled:bg-green-400">
        <span>
          <i class="fa-solid fa-paper-plane-top" />
        </span>
        &nbsp;&nbsp;Resend Email Invite
      </button>
    {/if}

    {#if docket.status == "COMPLETED" && docket.documentID}
      <div class="rounded-md bg-[#f7f7f7] p-4 dark:bg-[#292a2f]">
        <div class="flex w-full flex-col items-center justify-center space-y-3 rounded-md">
          <img src={qrImage} alt="qrcode" class="w-2/5 rounded-md" />
          <ShareComponent Link={docket.url} showOpenURL={true} />

          <div class="flex flex-col items-center justify-center gap-3 md:flex-row">
            <a href="#/analytics/documents/single?id={docket.documentID}" target="_blank" class="flex h-9 min-w-fit cursor-pointer items-center justify-center rounded-md border border-[#a0a1a580] bg-[#ff6a00] px-2 text-sm font-medium text-white hover:bg-[#ff8000] hover:text-white focus:outline-none active:bg-[#ff6a00] disabled:cursor-not-allowed disabled:bg-[#ff6a00] sm:ml-0 md:w-1/5">
              <i class="fa-solid fa-chart-mixed" />
              <span class="select-none">&nbsp;&nbsp;View&nbsp;Analytics&nbsp;</span>
            </a>
            <a href="#/analytics/documents/send-email?id={docket.documentID}" target="_blank" class="flex h-9 min-w-fit cursor-pointer items-center justify-center rounded-md border border-[#a0a1a580] bg-blue-500 px-2 text-sm font-medium text-white hover:bg-blue-600 hover:text-white focus:outline-none active:bg-blue-500 disabled:cursor-not-allowed disabled:bg-blue-200 sm:ml-0 md:w-1/5">
              <i class="fa-solid fa-envelope" />
              <span class="select-none">&nbsp;&nbsp;Send&nbsp;Email&nbsp;</span>
            </a>
          </div>
        </div>
      </div>
    {/if}

    {#if !showLoader}
      <Timeline {certData} {isApproversFound} {isESignPending} {isModeNotSelected} isPdfDigitallySigned={!docket.esign} publishID={docket.documentID} docketID={query.id} docCreated={docket.created} uuid={docket.uuid} {users} docStatus={docket.status} signCnMismatchID={docket.signCnMismatchID} on:copy={handleCopyInvite} on:cnameApproval={handleOpenCNModal} />
    {/if}
  </div>
</div>
